import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col } from 'react-bootstrap';
import { motion } from "framer-motion";
import Modal from "react-bootstrap/Modal";
import './index.scss';

import GaugeChart from 'react-gauge-chart'

import ProfileAvatar from "../../../../../components/ui/ProfileAvatar";
import Button from "../../../../../components/ui/Button";

import { useSelector, useDispatch } from 'react-redux';
import { getOrganizationHackerScore } from "../../../../../store/actions/dashboardAction";

import { useScanInfoContext } from "../../../../ScanInfo/ScanInfoContext";

import { getDomainImageUrl } from "../../../../../utils/util";
import SkeletonLoader from "../../../../../components/ui/SkeletonLoader";

const OrganizationHacker = () => {

    const organizationHackerScoreResponse = useSelector((state) => state?.dashboard?.organizationHackerScore)

    const dispatch = useDispatch();

    const {
        organizationId,
        triggerStatsFetch,
        setTriggerStatsFetch,
    } = useScanInfoContext();

    const [organizationHackerLoading, setOrganizationHackerLoading] = useState(false);
    const [showDomainListModal, setShowDomainListModal] = useState(false);
    const [selectedDomainList, setSelectedDomainList] = useState([]);
    const [selectedSeverity, setSelectedSeverity] = useState("");

    const chartStyle = {
        height: 60,
        width: 120,
    }

    const fetchOrganizationHackerScore = useCallback(()=>{        
        const params = organizationId.toLowerCase() !== "all" ? `&organization_id=${organizationId}`: ""
        if(organizationId.toLowerCase() !== "all") {
            setOrganizationHackerLoading(true)
            dispatch(getOrganizationHackerScore(params))
        }        
    },[organizationId, dispatch])

    useEffect(() => {
        if (triggerStatsFetch) {
            fetchOrganizationHackerScore()
            setTriggerStatsFetch(false);
        }
    }, [triggerStatsFetch, setTriggerStatsFetch, fetchOrganizationHackerScore]);

    useEffect(()=>{
        if(organizationHackerScoreResponse !== null) {
            setOrganizationHackerLoading(false)
        }
    },[organizationHackerScoreResponse])

    return (
        <>
            <div className="bg-white-with-shadow p-4 mt-4">
                {
                    organizationHackerLoading ?
                        <SkeletonLoader type={"line"} count={5} currentPage={"organizationPage"} />
                    :
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                    >
                        <div className="d-flex justify-content-between mb-4 dashboard-box-container">
                            <p className="heading font-bold">Organization Hacker Score</p>
                        </div>
                        <div className="d-flex score-containers w-100">
                            <div className="hacker-score-container d-flex">
                                <div className="dashboard-type-container hacker-score-container d-flex flex-column align-items-center p-3 h-100 w-75">
                                    <p className="hacker-score-heading mb-4">Overall score</p>
                                    <div className="total-records-container text-center mt-2">
                                        <p className="count-content">{organizationHackerScoreResponse?.stats?.overall_score || 0}</p>
                                        <p className="mb-4 hacker-score">Hacker score</p>
                                        <GaugeChart 
                                            id="gauge-chart1"
                                            nrOfLevels={4} 
                                            colors={["#ee6862", "#f2a16b", "#F4DA6B", "#8dd761"]}
                                            percent={organizationHackerScoreResponse?.stats?.overall_score/900 || 0}
                                            arcWidth={0.2}
                                            arcPadding={0}
                                            cornerRadius={0}
                                            hideText={true}
                                            style={chartStyle}
                                        />
                                    </div>
                                </div>
                                <div className="w-25">
                                    <div className="w-50 border-right h-100"></div>
                                </div>
                            </div>
                            <div className="business-impact-container">
                                <Row className="g-2 h-100">
                                    <Col lg={3}>
                                        <div className="dashboard-type-container p-3 bg-critical h-100 rounded">
                                            <p className="mb-2 font-bold">Critical</p>
                                            <div className="total-records-container">
                                                <p className="content">{organizationHackerScoreResponse?.stats?.domain_portfolio?.critical?.length || 0} <span>Domains</span></p>
                                                {
                                                    organizationHackerScoreResponse?.stats?.domain_portfolio?.critical?.length > 0 &&
                                                    organizationHackerScoreResponse?.stats?.domain_portfolio?.critical?.length <= 3 ?
                                                    organizationHackerScoreResponse?.stats?.domain_portfolio?.critical.map((data, index)=>{
                                                        return (
                                                            <div className="d-flex flex-row align-items-center justify-content-between py-2" key={index}>
                                                                <ProfileAvatar type={"domain"} name={data?.domain} imageUrl={getDomainImageUrl(data)} imageType={"square"}/>
                                                                <p className="mb-0 ms-3 font-bold">{data?.score}</p>
                                                            </div>
                                                        )
                                                    })
                                                    :
                                                    organizationHackerScoreResponse?.stats?.domain_portfolio?.critical.map((data, index)=>{
                                                        if(index <= 2) {
                                                            return (
                                                                <div className="d-flex flex-row align-items-center justify-content-between py-2" key={index}>
                                                                    <ProfileAvatar type={"domain"} name={data?.domain} imageUrl={getDomainImageUrl(data)} imageType={"square"}/>
                                                                    <p className="mb-0 ms-3 font-bold">{data?.score}</p>
                                                                </div>
                                                            )
                                                        }
                                                        return null
                                                    })
                                                }
                                                {organizationHackerScoreResponse?.stats?.domain_portfolio?.critical?.length > 3 && <p className="more mt-1" onClick={()=>{
                                                    setSelectedSeverity("critical")                                            
                                                    setSelectedDomainList(organizationHackerScoreResponse?.stats?.domain_portfolio?.critical)
                                                    setShowDomainListModal(true)
                                                }}>+{organizationHackerScoreResponse?.stats?.domain_portfolio?.critical?.length - 3} More</p>}
                                            </div>
                                        </div>
                                    </Col>

                                    <Col lg={3}>
                                        <div className="dashboard-type-container p-3 bg-high h-100 rounded">
                                            <p className="mb-2 font-bold">High</p>
                                            <div className="total-records-container">
                                                <p className="content">{organizationHackerScoreResponse?.stats?.domain_portfolio?.high?.length || 0} <span>Domains</span></p>
                                                {
                                                    organizationHackerScoreResponse?.stats?.domain_portfolio?.high?.length > 0 &&
                                                    organizationHackerScoreResponse?.stats?.domain_portfolio?.high?.length <= 3 ?
                                                    organizationHackerScoreResponse?.stats?.domain_portfolio?.high.map((data, index)=>{
                                                        return (
                                                            <div className="d-flex flex-row align-items-center justify-content-between py-2" key={index}>
                                                                <ProfileAvatar type={"domain"} name={data?.domain} imageUrl={getDomainImageUrl(data)} imageType={"square"}/>
                                                                <p className="mb-0 ms-3 font-bold">{data?.score}</p>
                                                            </div>
                                                        )
                                                    })
                                                    :
                                                    organizationHackerScoreResponse?.stats?.domain_portfolio?.high.map((data, index)=>{
                                                        if(index <= 2) {
                                                            return (
                                                                <div className="d-flex flex-row align-items-center justify-content-between py-2" key={index}>
                                                                    <ProfileAvatar type={"domain"} name={data?.domain} imageUrl={getDomainImageUrl(data)} imageType={"square"}/>
                                                                    <p className="mb-0 ms-3 font-bold">{data?.score}</p>
                                                                </div>
                                                            )
                                                        }
                                                        return null
                                                    })
                                                }
                                                {organizationHackerScoreResponse?.stats?.domain_portfolio?.high?.length > 3 && <p className="more mt-1" onClick={()=>{
                                                    setSelectedSeverity("high")                                            
                                                    setSelectedDomainList(organizationHackerScoreResponse?.stats?.domain_portfolio?.high)
                                                    setShowDomainListModal(true)
                                                }}>+{organizationHackerScoreResponse?.stats?.domain_portfolio?.high?.length - 3} More</p>}
                                            </div>
                                        </div>
                                    </Col>

                                    <Col lg={3}>
                                        <div className="dashboard-type-container p-3 bg-medium h-100 rounded">
                                            <p className="mb-2 font-bold">Medium</p>
                                            <div className="total-records-container">
                                                <p className="content">{organizationHackerScoreResponse?.stats?.domain_portfolio?.medium?.length || 0} <span>Domains</span></p>
                                                {
                                                    organizationHackerScoreResponse?.stats?.domain_portfolio?.medium?.length > 0 &&
                                                    organizationHackerScoreResponse?.stats?.domain_portfolio?.medium?.length <= 3 ?
                                                    organizationHackerScoreResponse?.stats?.domain_portfolio?.medium.map((data, index)=>{
                                                        return (
                                                            <div className="d-flex flex-row align-items-center justify-content-between py-2" key={index}>
                                                                <ProfileAvatar type={"domain"} name={data?.domain} imageUrl={getDomainImageUrl(data)} imageType={"square"}/>
                                                                <p className="mb-0 ms-3 font-bold">{data?.score}</p>
                                                            </div>
                                                        )
                                                    })
                                                    :
                                                    organizationHackerScoreResponse?.stats?.domain_portfolio?.medium.map((data, index)=>{
                                                        if(index <= 2) {
                                                            return (
                                                                <div className="d-flex flex-row align-items-center justify-content-between py-2" key={index}>
                                                                    <ProfileAvatar type={"domain"} name={data?.domain} imageUrl={getDomainImageUrl(data)} imageType={"square"}/>
                                                                    <p className="mb-0 ms-3 font-bold">{data?.score}</p>
                                                                </div>
                                                            )
                                                        }
                                                        return null
                                                    })
                                                }
                                                {organizationHackerScoreResponse?.stats?.domain_portfolio?.medium?.length > 3 && <p className="more mt-1" onClick={()=>{
                                                    setSelectedSeverity("medium")                                            
                                                    setSelectedDomainList(organizationHackerScoreResponse?.stats?.domain_portfolio?.medium)
                                                    setShowDomainListModal(true)
                                                }}>+{organizationHackerScoreResponse?.stats?.domain_portfolio?.medium?.length - 3} More</p>}
                                            </div>
                                        </div>
                                    </Col>

                                    <Col lg={3}>
                                        <div className="dashboard-type-container p-3 bg-low h-100 rounded">
                                            <p className="mb-2 font-bold">Low</p>
                                            <div className="total-records-container">
                                                <p className="content">{organizationHackerScoreResponse?.stats?.domain_portfolio?.low?.length || 0} <span>Domains</span></p>
                                                {
                                                    organizationHackerScoreResponse?.stats?.domain_portfolio?.low?.length > 0 &&
                                                    organizationHackerScoreResponse?.stats?.domain_portfolio?.low?.length <= 3 ?
                                                    organizationHackerScoreResponse?.stats?.domain_portfolio?.low.map((data, index)=>{
                                                        return (
                                                            <div className="d-flex flex-row align-items-center justify-content-between py-2" key={index}>
                                                                <ProfileAvatar type={"domain"} name={data?.domain} imageUrl={getDomainImageUrl(data)} imageType={"square"}/>
                                                                <p className="mb-0 ms-3 font-bold">{data?.score}</p>
                                                            </div>
                                                        )
                                                    })
                                                    :
                                                    organizationHackerScoreResponse?.stats?.domain_portfolio?.low.map((data, index)=>{
                                                        if(index <= 2) {
                                                            return (
                                                                <div className="d-flex flex-row align-items-center justify-content-between py-2" key={index}>
                                                                    <ProfileAvatar type={"domain"} name={data?.domain} imageUrl={getDomainImageUrl(data)} imageType={"square"}/>
                                                                    <p className="mb-0 ms-3 font-bold">{data?.score}</p>
                                                                </div>
                                                            )
                                                        }
                                                        return null
                                                    })
                                                }
                                                {organizationHackerScoreResponse?.stats?.domain_portfolio?.low?.length > 3 && <p className="more mt-1" onClick={()=>{
                                                    setSelectedSeverity("low")                                            
                                                    setSelectedDomainList(organizationHackerScoreResponse?.stats?.domain_portfolio?.low)
                                                    setShowDomainListModal(true)
                                                }}>+{organizationHackerScoreResponse?.stats?.domain_portfolio?.low?.length - 3} More</p>}
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        </div>   
                    </motion.div>
                }
                <Modal
                    show={showDomainListModal}
                    onHide={()=>setShowDomainListModal(false)}
                    size="md"
                    centered
                    className=""
                    >
                        <Modal.Body>                                
                            <div className="delete-organization-container severity-domain-list">
                                <h4>Domain List</h4>
                                <div className={`p-4 m-2 rounded ${selectedSeverity}`}>
                                    {
                                        selectedDomainList.map((data, index)=>{
                                            return (
                                                <div className="d-flex flex-row align-items-center justify-content-between py-3 border-bottom" key={index}>
                                                    <ProfileAvatar type={"domain"} name={data?.domain} imageUrl={getDomainImageUrl(data)} imageType={"square"}/>
                                                    <p className="mb-0 ms-3 font-bold">{data?.score}</p>
                                                </div>
                                            )
                                        })
                                    }
                                </div>
                                <div className="d-flex justify-content-center gap-3 m-2">
                                    <div className="w-50">                                                
                                        <Button buttonText="Close" buttonType={"secondary"} buttonClicked={()=>setShowDomainListModal(false)}/>
                                    </div>
                                </div>
                            </div>
                        </Modal.Body>
                </Modal>               
            </div>
        </>
    );
};

export default OrganizationHacker;
